// Enum value lists — keep IDENTICAL to the Postgres enum types defined in
// `supabase/migrations/00001_init.sql` (sub-plan 02 lines 102-123). Each
// `as const` array is exported alongside its inferred TS union type.
//
// DB enum count: 8 (user_role, job_status, ticket_status, engineer_approval,
// engineer_qual_level, customer_level, payment_kind, notification_kind).
// Total enumeration entries across the 8 enums: 3+7+4+3+3+3+3+6 = 32.

export const userRole = ["customer", "engineer", "admin"] as const;
export type UserRole = (typeof userRole)[number];

// P4 (migration 00020) adds 'in_progress' (was missing) and
// 'pending_customer_confirmation'. Order in array matches the FSM flow.
export const jobStatus = [
  "draft",
  "published",
  "accepted",
  "travelling",
  "on_site",
  "in_progress",
  "pending_customer_confirmation",
  "completed",
  "cancelled",
] as const;
export type JobStatus = (typeof jobStatus)[number];

export const ticketStatus = [
  "open",
  "in_progress",
  "resolved",
  "closed",
] as const;
export type TicketStatus = (typeof ticketStatus)[number];

export const engineerApproval = ["pending", "approved", "rejected"] as const;
export type EngineerApproval = (typeof engineerApproval)[number];

export const engineerQualLevel = ["primary", "senior", "expert"] as const;
export type EngineerQualLevel = (typeof engineerQualLevel)[number];

export const customerLevel = ["silver", "gold", "diamond"] as const;
export type CustomerLevel = (typeof customerLevel)[number];

export const paymentKind = ["credit_card", "bank_transfer", "cash"] as const;
export type PaymentKind = (typeof paymentKind)[number];

// `account` (R19) was added to align with sub-plan 02 line 117-122
// notification_kind enum — phase-2 GDPR pre-deletion warning cron emits
// rows of this kind. Forward-compat in phase-1 (no insert site yet).
export const notificationKind = [
  "job_status",
  "promotion",
  "billing",
  "approval",
  "system",
  "account",
] as const;
export type NotificationKind = (typeof notificationKind)[number];

// ---------- Auxiliary string-literal sets used by api/* schemas ----------
//
// These mirror inline CHECK constraints in 00001_init.sql or the discriminator
// strings carried over the wire. Exported as `as const` so we get both runtime
// arrays (consumable by `z.enum(...)`) and a narrow TS type.

// job_photos.kind CHECK (sub-plan 02 line 331)
export const jobPhotoKind = ["request", "completion"] as const;
export type JobPhotoKind = (typeof jobPhotoKind)[number];

// engineer_qualifications.kind CHECK (sub-plan 02 line 183)
export const engineerQualificationKind = ["photo_id", "qualification"] as const;
export type EngineerQualificationKind =
  (typeof engineerQualificationKind)[number];

// ratings.kind CHECK (sub-plan 02 line 348)
export const ratingKind = [
  "customer_to_engineer",
  "engineer_to_customer",
] as const;
export type RatingKind = (typeof ratingKind)[number];

// PhoneStartOtpReq.purpose enum (sub-plan 03 line 260)
export const otpPurpose = ["login", "register", "reset"] as const;
export type OtpPurpose = (typeof otpPurpose)[number];

// admin_permissions.role_label CHECK (sub-plan 02 line 501)
export const adminRoleLabel = [
  "Administrator",
  "Event user",
  "Normal User",
] as const;
export type AdminRoleLabel = (typeof adminRoleLabel)[number];

// AdminSendPushReq.kind subset (sub-plan 03 §"Acceptance Criteria" line 549)
// — admin broadcast UI exposes only these two kinds (A13 dropdown). The other
// 4 (`job_status`, `approval`, `billing`, `account`) are reserved for system
// emitters and MUST NOT be injected via the broadcast endpoint.
export const adminBroadcastPushKind = ["promotion", "system"] as const;
export type AdminBroadcastPushKind = (typeof adminBroadcastPushKind)[number];

// job_extras.status enum (P3 migration 00021).
export const jobExtraStatus = ["proposed", "approved", "rejected"] as const;
export type JobExtraStatus = (typeof jobExtraStatus)[number];

// wallet_transactions.type enum. Created in 00017_wallet_payment.sql; this
// const has been missing from the shared schemas. P5 (migration 00022) adds
// 'withdrawal' to the DB enum — formalised here as the source of truth.
export const walletTxnType = [
  "topup",
  "payment",
  "refund",
  "adjustment",
  "withdrawal",
] as const;
export type WalletTxnType = (typeof walletTxnType)[number];

// withdrawal_requests.status enum (P5 migration 00022b).
export const withdrawalStatus = [
  "requested",
  "processing",
  "completed",
  "rejected",
  "cancelled",
] as const;
export type WithdrawalStatus = (typeof withdrawalStatus)[number];

// tickets.ticket_kind CHECK (P4 migration 00020b — column owned logically by
// P6 but added in P4 because autoConfirmJobs cron filters on it). Values:
//   general    — default; legacy tickets and most user-opened support tickets
//   dispute    — P6 "report an issue" on a completed job (must skip auto-confirm)
//   feedback   — reserved for phase-2 NPS / structured feedback flows
export const ticketKind = ["general", "dispute", "feedback"] as const;
export type TicketKind = (typeof ticketKind)[number];

// AdminSendEmailReq / AdminSendPushReq.user_group enum (sub-plan 03 line 549)
//
// "Primary" / "Secondary" are kept as engineer-only for backward compat
// with existing broadcasts. Migration 00034 added customer_profiles.group_name,
// so customers can also be tagged Primary/Secondary — the new "Primary
// Customers" / "Secondary Customers" groups address them specifically without
// changing the meaning of the existing labels.
export const adminBroadcastUserGroup = [
  "Primary",
  "Secondary",
  "Primary Customers",
  "Secondary Customers",
  "All Engineers",
  "All Customers",
  "All",
] as const;
export type AdminBroadcastUserGroup = (typeof adminBroadcastUserGroup)[number];

// App language code (i18n sub-plan 02). Single source of truth for the
// literal union shared by the API (i18n module) and the mobile app.
//
// Adding a new locale: add the literal here, add the CHECK-constraint value
// in a new migration, and add the catalog folder to
// apps/mobile/src/i18n/locales/.
export const language = ["en", "zh-Hant"] as const;
export type Language = (typeof language)[number];
